import { Handshake, Plus, Trash2, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Admin > Sponsors panel. Extracted from Admin.jsx (Iter48).
 * All state + handlers live in the parent; this is pure presentational.
 */
export default function AdminSponsors({
  sponsors,
  form,
  setForm,
  fileRef,
  onCreate,
  onDelete,
  onReorder,
}) {
  return (
    <section className="space-y-6" data-testid="admin-sponsors">
      {/* Add sponsor */}
      <div className="border border-[#232330] bg-[#101015] p-5">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#F4D03F] mb-3 flex items-center gap-2">
          <Handshake className="w-3.5 h-3.5" /> Add sponsor / partner
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[1.2fr_1.5fr_100px_auto_auto] gap-3 items-end">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-gray-500 mb-1 block">Title</Label>
            <Input
              data-testid="sponsor-form-title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Emergent Labs"
              className="rounded-none bg-[#05050A] border-[#232330] font-mono"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-gray-500 mb-1 block">URL</Label>
            <Input
              data-testid="sponsor-form-url"
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              placeholder="https://emergent.sh"
              className="rounded-none bg-[#05050A] border-[#232330] font-mono"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-gray-500 mb-1 block">Order</Label>
            <Input
              data-testid="sponsor-form-order"
              type="number"
              value={form.sort_order}
              onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) || 0 })}
              className="rounded-none bg-[#05050A] border-[#232330] font-mono"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-gray-500 mb-1 block">Logo (≤500KB)</Label>
            <input
              ref={fileRef}
              data-testid="sponsor-form-file"
              type="file"
              accept="image/*"
              onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })}
              className="text-xs font-mono text-gray-400 file:mr-3 file:py-2 file:px-3 file:rounded-none file:border file:border-[#232330] file:bg-transparent file:text-cyan-400 file:font-bold file:cursor-pointer"
            />
          </div>
          <Button
            onClick={onCreate}
            data-testid="sponsor-form-submit"
            className="rounded-none bg-[#F4D03F] hover:bg-yellow-300 text-black uppercase tracking-widest text-xs font-bold"
          >
            <Plus className="w-3.5 h-3.5 mr-2" /> Add sponsor
          </Button>
        </div>
      </div>

      {/* Sponsor list */}
      <div className="space-y-2">
        <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 px-1">
          Active sponsors · {sponsors.length}
        </div>
        {sponsors.length === 0 && (
          <div className="border border-[#232330] p-8 text-center text-gray-500 font-mono text-xs uppercase tracking-widest">
            No sponsors yet. Add your first partner above.
          </div>
        )}
        {sponsors.map((s) => (
          <div
            key={s.id}
            data-testid={`sponsor-row-${s.id}`}
            className="border border-[#232330] bg-[#101015] p-4 flex flex-wrap items-center justify-between gap-4"
          >
            <div className="flex items-center gap-4 min-w-0 flex-1">
              <div className="w-16 h-16 bg-[#05050A] border border-[#232330] flex items-center justify-center shrink-0">
                <img src={s.logo_url} alt={s.title} className="max-w-full max-h-full object-contain" />
              </div>
              <div className="min-w-0">
                <div className="font-heading font-black uppercase tracking-tight text-lg truncate">
                  {s.title}
                </div>
                <a
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-cyan-400 hover:text-cyan-300 font-mono truncate flex items-center gap-1"
                >
                  {s.url} <ExternalLink className="w-3 h-3" />
                </a>
                <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mt-0.5">
                  order {s.sort_order || 0}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => onReorder(s, -1)}
                data-testid={`sponsor-up-${s.id}`}
                className="text-[10px] font-mono uppercase tracking-widest text-gray-400 border border-[#232330] hover:border-cyan-400 hover:text-cyan-400 px-2 py-1"
                aria-label="Move up"
              >
                ↑
              </button>
              <button
                onClick={() => onReorder(s, 1)}
                data-testid={`sponsor-down-${s.id}`}
                className="text-[10px] font-mono uppercase tracking-widest text-gray-400 border border-[#232330] hover:border-cyan-400 hover:text-cyan-400 px-2 py-1"
                aria-label="Move down"
              >
                ↓
              </button>
              <Button
                size="sm"
                onClick={() => onDelete(s)}
                data-testid={`sponsor-delete-${s.id}`}
                variant="outline"
                className="rounded-none border-[#232330] hover:border-red-500 hover:text-red-500 uppercase tracking-widest text-xs"
              >
                <Trash2 className="w-3.5 h-3.5 mr-2" /> Remove
              </Button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
